import { DestroyRef, effect, inject, Injectable, isDevMode, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from './user.service';
import { User, UserSettings } from '../models';

/** Refresh the Mercure subscribe token this long before it expires. */
const REFRESH_BEFORE_EXPIRY = 60 * 60 * 1000;
/** Never refresh more often than this, so a short-lived token cannot cause a refresh loop. */
const MIN_REFRESH_GAP = 60 * 1000;
/** setTimeout fires immediately for delays above ~24.8 days; wait at most a day and check again. */
const MAX_TIMER_DELAY = 24 * 60 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class ApplicationUserService {
  userSubject = new BehaviorSubject<User | null>(null);
  #userService = inject(UserService);
  #translateService = inject(TranslateService);
  userSettingsSubject = new BehaviorSubject<UserSettings | null>(null);
  userSettings$ = this.userSettingsSubject.asObservable();
  user$ = this.userSubject.asObservable();
  user = signal<User | null>(null);
  userSettings = signal<UserSettings | null>(null);
  expirationTime = signal(new Date().getTime());
  /** Why the last attempt to load the user failed; null while loading or once loaded. */
  loadError = signal<unknown>(null);
  #refreshTimer?: ReturnType<typeof setTimeout>;
  #lastRefreshAt = 0;

  constructor() {
    this.user$.subscribe((value) => {
      this.user.set(value);
      this.userSettings.set(value ? value.settings : null);
    });

    // Keep the Mercure subscribe token fresh: one timer, replaced whenever the user (and so the token) changes.
    effect(() => {
      const token = this.user()?.mercureSubscribeToken;
      this.#scheduleTokenRefresh(token ? new Date(token.expiresAt).getTime() : null);
    });
    inject(DestroyRef).onDestroy(() => clearTimeout(this.#refreshTimer));
  }

  #scheduleTokenRefresh(expiresAt: number | null): void {
    clearTimeout(this.#refreshTimer);
    this.#refreshTimer = undefined;
    if (expiresAt === null) return;

    this.expirationTime.set(expiresAt);
    const due = Math.max(expiresAt - REFRESH_BEFORE_EXPIRY, this.#lastRefreshAt + MIN_REFRESH_GAP);
    const delay = Math.min(Math.max(due - Date.now(), 0), MAX_TIMER_DELAY);

    this.#refreshTimer = setTimeout(() => {
      if (Date.now() < due) {
        this.#scheduleTokenRefresh(expiresAt); // the wait was capped; check again
      } else {
        this.#refreshUser(expiresAt);
      }
    }, delay);
  }

  /** Reloads the user, which carries a new token; a failed reload is retried after MIN_REFRESH_GAP. */
  #refreshUser(expiresAt: number): void {
    this.#lastRefreshAt = Date.now();
    this.#userService.getUserMe().subscribe({
      next: (user) => {
        this.userSubject.next(user);
        this.userSettingsSubject.next(user.settings);
      },
      error: () => this.#scheduleTokenRefresh(expiresAt),
    });
  }

  setApplicationUser(onError?: (err: unknown) => void): void {
    // also before the user arrives, so a failure can be shown translated
    this.#translateService.setDefaultLang('nl');
    this.loadError.set(null);
    this.#userService.getUserMe().subscribe({
      next: (user) => {
        // sessionStorage.setItem('user', JSON.stringify(user));
        // this.#translateService.setDefaultLang(user.settings.language);
        this.userSubject.next(user);
        this.userSettingsSubject.next(user.settings);
      },
      error: (err) => {
        // always surfaced, so the app can show it instead of loading forever; the callback (e.g. a login redirect)
        // is skipped in dev mode
        this.loadError.set(err);
        if (!isDevMode()) onError?.(err);
      },
    });
  }
}
